import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { ProcessDataType } from './process-data-type';
export { ProcessDataType };
import { processDataTypeSchema } from '@schemas/business-objects/process-data-type';
import { ProcessDataPropertyFormArray } from './process-data-property-form-group';
import { ProcessDataValueFormArray } from './process-data-value-form-group';
import { ProcessFunctionFormArray } from './process-function-form-group';
import { ProcessFunctionParameterFormArray } from './process-function-parameter-form-group';
import { ProcessMessagePropertyFormArray } from './process-message-property-form-group';
import { ProcessServicePropertyFormArray } from './process-service-property-form-group';
import { ProcessUserInterfacePropertyFormArray } from './process-user-interface-property-form-group';

export class ProcessDataTypeFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    displayName: SchemaFormControl;
    description: SchemaFormControl;
    nativeType: SchemaFormControl;
    values: ProcessDataValueFormArray;
    properties: ProcessDataPropertyFormArray;
    usedByDataProperties: ProcessDataPropertyFormArray;
    usedByUIProperties: ProcessUserInterfacePropertyFormArray;
    usedByServiceProperties: ProcessServicePropertyFormArray;
    usedByMessageProperties: ProcessMessagePropertyFormArray;
    usedByFunctionParameters: ProcessFunctionParameterFormArray;
    usedByFunctions: ProcessFunctionFormArray;
  };

  declare value: Partial<ProcessDataType>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(processDataTypeSchema, ProcessDataType, data, options);
  }

  get object(): ProcessDataType {
    return new ProcessDataType(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'values':
        return new ProcessDataValueFormArray();
      case 'properties':
        return new ProcessDataPropertyFormArray();
      case 'usedByDataProperties':
        return new ProcessDataPropertyFormArray();
      case 'usedByUIProperties':
        return new ProcessUserInterfacePropertyFormArray();
      case 'usedByServiceProperties':
        return new ProcessServicePropertyFormArray();
      case 'usedByMessageProperties':
        return new ProcessMessagePropertyFormArray();
      case 'usedByFunctionParameters':
        return new ProcessFunctionParameterFormArray();
      case 'usedByFunctions':
        return new ProcessFunctionFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<ProcessDataType[]> {
    return ProcessDataType.find(filter);
  }

  static async findOne(filter?: any): Promise<ProcessDataType | null> {
    return ProcessDataType.findOne(filter);
  }

  static async findById(id: string, filter?: any): Promise<ProcessDataType> {
    return ProcessDataType.findById(id, filter);
  }

  static async create(
    data: Partial<ProcessDataType>,
  ): Promise<ProcessDataType> {
    return ProcessDataType.create(data as any);
  }

  static async updateById(
    id: string,
    updates: Partial<ProcessDataType>,
  ): Promise<ProcessDataType> {
    return ProcessDataType.updateById(id, updates as any);
  }

  static async deleteById(id: string): Promise<boolean> {
    return ProcessDataType.deleteById(id);
  }
}

export class ProcessDataTypeFormArray extends PersistedFormArray {
  declare controls: ProcessDataTypeFormGroup[];

  readonly entityName = 'ProcessDataType' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(processDataTypeSchema, ProcessDataType, options);
  }

  protected override createItemGroup() {
    return new ProcessDataTypeFormGroup();
  }

  get array(): ProcessDataType[] {
    return this.controls.map((group: ProcessDataTypeFormGroup) => group.object);
  }
}

export class ProcessDataTypePersistedArray extends PersistedArray<ProcessDataType> {
  readonly entityName = 'ProcessDataType' as const;

  constructor(options?: PersistedArrayOptions) {
    super(ProcessDataType, options);
  }
}
